import { useEffect, useRef, useState, type FormEvent } from "react";
import { useClientSelection } from "../../client-context";
import { Link } from "react-router-dom";
import { ExternalLink, Globe, Instagram, Facebook, Lightbulb, Play, RefreshCw, Search, Sparkles, X } from "lucide-react";
import { api, formatDate } from "../../api";
import { useApi } from "../../hooks";
import { compactCount, type Competitor } from "../../competitors";
import { embedSrc, mediaSrc } from "./media";

/**
 * Competitors' own posts, their outliers, and finding competitors (Radar's model, for a
 * local business): utils/competitorSocial.js. Nothing here spends credits on its own;
 * reading posts and searching are buttons that say what they cost.
 */

export type SocialPost = {
  id: number; competitor_id: number; competitor_label: string; competitor_kind: "competitor" | "self";
  platform: "instagram" | "facebook" | "tiktok"; shortcode: string | null; url: string | null; caption: string | null;
  format: "reel" | "video" | "carousel" | "image" | "text"; thumbnail_url: string | null; video_url: string | null;
  views: number | null; likes: number | null; comments: number | null; shares: number | null;
  is_pinned: boolean; is_paid: boolean; posted_at: string | null; first_seen_at: string; last_seen_at: string;
  metric: "views" | "likes" | "engagement"; value: number | null;
  /** How far it beat the usual. `basis` (Viral posts only): against the account's followers, or the search's typical post. */
  outlier: { ratio: number; metric: string; value: number; usual: number; samples: number; basis?: "followers" | "typical" | null } | null;
  analysis: { hook: string; why: string; idea: string } | null;
  /** Viral posts only: the account's followers, who the post speaks to, and whether it names the client's towns. */
  author_followers?: number | null; audience?: "customers" | "owners" | "other" | null; near_you?: boolean;
  /** Viral posts library: first found in the last week. */
  is_new?: boolean;
};
/** Where a post's AI actions and "Save as idea" go: a competitor's post, or one from Viral posts. */
export type PostSource = { kind: "competitor" | "viral"; api: string };
const COMPETITOR_SOURCE: PostSource = { kind: "competitor", api: "/api/competitors/posts" };
type PostsResponse = { posts: SocialPost[]; counts: { total: number; outliers: number } };
type Candidate = {
  key: string; platform: "instagram" | "facebook" | "website"; name: string; instagram_handle?: string; page_id?: string;
  followers?: number | null; likes?: number | null; posts?: number | null; ads?: number; about: string | null;
  category?: string | null; website?: string | null; verified?: boolean; fit: "direct" | "nearby" | "brand" | null; reason: string | null;
  source_url?: string; source?: string; local_presence_verified?: boolean;
};
type Find = { id: number; query: string; results: Candidate[]; credits_used: number | null; ranked_by_ai: boolean; created_at: string; notes?: string[] };

const METRIC_WORD: Record<string, string> = { views: "views", likes: "likes", engagement: "reactions, comments and shares" };
const FORMAT_WORD: Record<string, string> = { reel: "Reel", video: "Video", carousel: "Carousel", image: "Photo", text: "Text" };
const FIT_WORD: Record<string, string> = { direct: "Direct competitor", nearby: "Nearby or related", brand: "Big brand" };
const PLATFORM_WORD: Record<string, string> = { instagram: "Instagram", facebook: "Facebook", tiktok: "TikTok" };

/** Lucide has no TikTok mark; this one is drawn at the same size and colour as its icons. */
function TikTokIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-1.81-2.47V9.76a5.73 5.73 0 1 0 4.9 5.67V9.01a7.3 7.3 0 0 0 4.27 1.37V7.29a4.28 4.28 0 0 1-3.21-1.47z" />
    </svg>
  );
}
export function PlatformIcon({ platform }: { platform: SocialPost["platform"] }) {
  return platform === "instagram" ? <Instagram aria-hidden="true" /> : platform === "tiktok" ? <TikTokIcon /> : <Facebook aria-hidden="true" />;
}

/** The badge on a card: "99.9x followers" (views against the account's size), "3x typical" or "2.4x usual". */
const badgeOf = (o: NonNullable<SocialPost["outlier"]>) => `${o.ratio}x ${o.basis === "followers" ? "followers" : o.basis === "typical" ? "typical" : "usual"}`;

/** Instagram's image CDN refuses other sites' pages, so images come through our own origin. */
const ago = (iso: string | null) => {
  if (!iso) return "";
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86400000);
  return days < 1 ? "today" : days < 7 ? `${days}d ago` : days < 60 ? `${Math.floor(days / 7)}w ago` : `${Math.floor(days / 30)}mo ago`;
};

const count = (n: number, one: string, many: string) => `${compactCount(n)} ${n === 1 ? one : many}`;
function Figures({ p }: { p: SocialPost }) {
  return (
    <span className="cs-figs">
      {p.views !== null && <span>{count(p.views, "view", "views")}</span>}
      {p.likes !== null && <span>{p.platform === "facebook" ? count(p.likes, "reaction", "reactions") : count(p.likes, "like", "likes")}</span>}
      {p.comments !== null && <span>{count(p.comments, "comment", "comments")}</span>}
    </span>
  );
}

export function PostsView({ clientId, competitors, canChange, outliersOnly, onCompetitorsChanged }: {
  clientId: number; competitors: Competitor[]; canChange: boolean; outliersOnly: boolean; onCompetitorsChanged: () => void;
}) {
  const [who, setWho] = useState("");
  const [platform, setPlatform] = useState("");
  const [format, setFormat] = useState("");
  const [scope, setScope] = useState<"competitors" | "self">("competitors");
  const [sort, setSort] = useState(outliersOnly ? "outliers" : "newest");
  const qs = new URLSearchParams({ client_id: String(clientId), scope, sort, ...(who ? { competitor_id: who } : {}), ...(platform ? { platform } : {}), ...(format ? { format } : {}), ...(outliersOnly ? { outliers: "1" } : {}) });
  const { data, loading, error, reload } = useApi<PostsResponse>(`/api/competitors/posts?${qs}`);
  const [open, setOpen] = useState<SocialPost | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const readable = competitors.filter((c) => c.instagram_handle || c.page_url);
  const missing = competitors.filter((c) => !c.instagram_handle && c.kind !== "self");
  const own = competitors.find((c) => c.kind === "self");
  async function refreshAll() {
    setBusy(true); setNotice(null);
    try {
      const r = await api<{ results: Array<{ label: string; status: string; partial?: boolean; detail?: string; credits?: number; posts_read?: number }> }>("/api/competitors/posts/refresh", { method: "POST", body: JSON.stringify({ client_id: clientId, include_reels: true }) });
      const read = r.results.reduce((n, x) => n + (x.posts_read || 0), 0);
      const credits = r.results.reduce((n, x) => n + (x.credits || 0), 0);
      const failed = r.results.filter((x) => x.status === "blocked" || x.status === "error" || x.status === "deferred" || x.partial);
      setNotice({ ok: !failed.length, text: `${read} posts read from ${r.results.length} ${r.results.length === 1 ? "account" : "accounts"} for ${credits} credits.${failed.length ? ` Could not read ${failed.map((f) => f.label).join(", ")}: ${failed[0].detail || "try again"}` : ""}` });
      reload(); onCompetitorsChanged();
    } catch (e) { setNotice({ ok: false, text: e instanceof Error ? e.message : "Could not read their posts." }); }
    finally { setBusy(false); }
  }
  const posts = data?.posts || [];
  return (
    <div className="cs-view">
      <div className="cs-toolbar">
        <div className="segmented-control compact" aria-label="Whose posts">
          <button type="button" className={scope === "competitors" ? "active" : ""} aria-pressed={scope === "competitors"} onClick={() => setScope("competitors")}>Competitors</button>
          <button type="button" className={scope === "self" ? "active" : ""} aria-pressed={scope === "self"} disabled={!own} title={own ? undefined : "Add the client's own page on Compare first"} onClick={() => setScope("self")}>Yours</button>
        </div>
        {scope === "competitors" && (
          <select aria-label="Competitor" value={who} onChange={(e) => setWho(e.target.value)}>
            <option value="">All competitors</option>
            {competitors.filter((c) => c.kind !== "self").map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        )}
        <select aria-label="Platform" value={platform} onChange={(e) => setPlatform(e.target.value)}>
          <option value="">Instagram and Facebook</option><option value="instagram">Instagram</option><option value="facebook">Facebook</option>
        </select>
        <select aria-label="Content" value={format} onChange={(e) => setFormat(e.target.value)}>
          <option value="">Posts and reels</option><option value="reel">Reels</option><option value="post">Posts</option>
        </select>
        {!outliersOnly && (
          <select aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="newest">Newest</option><option value="top">Most views or likes</option><option value="outliers">Most above their usual</option>
          </select>
        )}
        <span className="cs-toolbar-end">
          {data && <span className="muted">{outliersOnly ? `${data.counts.outliers} viral ${data.counts.outliers === 1 ? "post" : "posts"}` : `${data.counts.total} ${format === "reel" ? (data.counts.total === 1 ? "reel" : "reels") : (data.counts.total === 1 ? "post" : "posts")}`}</span>}
          {canChange && <button type="button" className="button secondary" disabled={busy || !readable.length} title="Reads posts and reels: up to 2 credits per Instagram account and 3 per Facebook page on its first read." onClick={() => void refreshAll()}><RefreshCw className={busy ? "spin" : ""} aria-hidden="true" />{busy ? "Reading posts and reels…" : "Read posts and reels"}</button>}
        </span>
      </div>
      {outliersOnly && <p className="cs-note">A viral post here is one with at least twice the views (reels) or likes (photos) of that account's usual post. Paid partnerships and pinned posts are left out.</p>}
      {notice && <p className={`inline-notice ${notice.ok ? "success" : "danger"}`} role="status"><span>{notice.text}</span><button type="button" aria-label="Dismiss" onClick={() => setNotice(null)}>×</button></p>}
      {missing.length > 0 && scope === "competitors" && <p className="cs-note">{missing.length === 1 ? `${missing[0].label} has` : `${missing.length} competitors have`} no Instagram handle yet. Add it from the row menu on Compare ("Edit") to see their posts.</p>}
      {error ? <p className="inline-notice danger">{error}</p> : loading && !data ? <p className="muted cs-pad">Loading posts…</p> : !posts.length ? (
        <div className="cs-empty">
          <strong>{outliersOnly ? "No viral posts yet" : "No posts or reels yet"}</strong>
          <p className="muted">{!competitors.some((c) => c.kind !== "self") ? <>No competitors added yet. <Link className="text-button" to={`/competitors?client_id=${clientId}&view=find`}>Find competitors</Link> suggests local ones to add.</> : !readable.length ? "Add an Instagram handle or Facebook page to a competitor, then press Read posts and reels." : outliersOnly ? "They show once an account has at least 6 posts read and one beats its usual by 2 times or more." : "Press Read posts and reels to pull their newest posts."}</p>
        </div>
      ) : (
        <div className="cs-grid">
          {posts.map((p) => <PostCard key={p.id} p={p} onOpen={() => setOpen(p)} />)}
        </div>
      )}
      {open && <PostLightbox p={open} clientId={clientId} source={COMPETITOR_SOURCE} canChange={canChange} onClose={() => setOpen(null)} onAnalyzed={(analysis) => { setOpen({ ...open, analysis }); reload(); }} />}
    </div>
  );
}

export function PostCard({ p, onOpen }: { p: SocialPost; onOpen: () => void }) {
  const src = mediaSrc(p);
  const [broken, setBroken] = useState(false);
  const picture = src && !broken ? src : null;
  const play = (p.format === "reel" || p.format === "video") && <Play className="cs-play" aria-hidden="true" />;
  const badge = p.outlier && <span className="cs-badge">{badgeOf(p.outlier)}</span>;
  // "Found this week": first found in the last 7 days (Viral posts library), not when it was posted.
  const flags = [p.is_new && "Found this week", p.near_you && "Near you", p.audience === "owners" && "For business owners", p.audience === "other" && "Off topic", p.is_paid && "Paid partnership", p.is_pinned && "Pinned"].filter((f): f is string => Boolean(f));
  return (
    <button type="button" className="cs-card" onClick={onOpen} aria-label={`${p.competitor_label}: ${FORMAT_WORD[p.format]} ${ago(p.posted_at)}`}>
      {picture ? (
        <span className="cs-thumb">
          <img src={picture} alt="" loading="lazy" onError={() => setBroken(true)} />
          {play}{badge}
        </span>
      ) : (
        // No picture: the badge and play mark get their own line above the caption instead of covering its first words.
        <span className="cs-thumb cs-thumb-plain">
          {(badge || play) && <span className="cs-thumb-head">{badge}{play}</span>}
          <span className="cs-thumb-text">{(p.caption || "No preview").slice(0, 140)}</span>
        </span>
      )}
      <span className="cs-card-body">
        <span className="cs-card-who"><PlatformIcon platform={p.platform} /><strong>{p.competitor_label}</strong><span className="muted">{FORMAT_WORD[p.format]}</span><span className="muted">{ago(p.posted_at)}</span></span>
        <Figures p={p} />
        {/* A card with no picture already shows the caption above. */}
        {p.caption && picture && <span className="cs-caption">{p.caption}</span>}
        {flags.length > 0 && <span className="muted cs-flag">{flags.map((f) => <span key={f}>{f}</span>)}</span>}
      </span>
    </button>
  );
}

export function PostLightbox({ p, clientId, source, canChange, onClose, onAnalyzed }: {
  p: SocialPost; clientId: number; source: PostSource; canChange: boolean; onClose: () => void; onAnalyzed: (a: SocialPost["analysis"]) => void;
}) {
  const [videoFailed, setVideoFailed] = useState(false);
  const [busy, setBusy] = useState<"" | "analyze" | "draft" | "idea">("");
  const [err, setErr] = useState("");
  const [draft, setDraft] = useState<{ url: string; name: string } | null>(null);
  const [idea, setIdea] = useState<{ url: string; existing: boolean; name: string } | null>(null);
  // "Make this for…": this client, the agency's own brand, or any other client. Drafts and ideas go there.
  const { clients } = useClientSelection();
  const [target, setTarget] = useState(clientId);
  const brands = (clients.data || [])
    .filter((x) => x.status !== "churned")
    .sort((a, b) => Number(b.id === clientId) - Number(a.id === clientId) || Number(b.industry === "Agency") - Number(a.industry === "Agency") || a.name.localeCompare(b.name));
  const brandName = (id: number) => brands.find((x) => x.id === id)?.name || "this client";
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  const src = mediaSrc(p);
  // A signed video link lives about a day; past that the poster and a link out stand in.
  const fresh = p.video_url && Date.now() - Date.parse(p.last_seen_at) < 20 * 3600000;
  const embed = embedSrc(p);
  async function run(kind: "analyze" | "draft" | "idea") {
    setBusy(kind); setErr("");
    try {
      if (kind === "analyze") onAnalyzed((await api<{ analysis: SocialPost["analysis"] }>(`${source.api}/${p.id}/analyze`, { method: "POST", body: JSON.stringify({}) })).analysis);
      else if (kind === "draft") {
        const r = await api<{ url: string; client_name?: string }>(`${source.api}/${p.id}/draft`, { method: "POST", body: JSON.stringify({ for_client_id: target }) });
        setDraft({ url: r.url, name: r.client_name || brandName(target) });
      } else {
        const r = await api<{ url: string; existing?: boolean; client_name?: string }>(`/api/content-ideas/${target}/from-post`, { method: "POST", body: JSON.stringify({ source: source.kind, post_id: p.id }) });
        setIdea({ url: r.url, existing: Boolean(r.existing), name: r.client_name || brandName(target) });
      }
    } catch (e) { setErr(e instanceof Error ? e.message : "That did not work."); }
    finally { setBusy(""); }
  }
  return (
    <div className="cs-lightbox" role="dialog" aria-modal="true" aria-label={`${p.competitor_label}'s post`} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cs-lightbox-panel">
        <button type="button" className="icon-button cs-lightbox-close" aria-label="Close" onClick={onClose}><X aria-hidden="true" /></button>
        <div className="cs-lightbox-media">
          {fresh && !videoFailed
            ? <video src={p.video_url!} poster={src || undefined} controls autoPlay playsInline onError={() => setVideoFailed(true)} />
            : embed
              ? <iframe className={`cs-embed cs-embed-${p.platform}`} src={embed} title={`${p.competitor_label}'s video`} allow="autoplay; encrypted-media; fullscreen; picture-in-picture" allowFullScreen />
              : src ? <img src={src} alt="" /> : <p className="cs-thumb-text">{p.caption || "No preview"}</p>}
        </div>
        <div className="cs-lightbox-side">
          <p className="cs-card-who"><PlatformIcon platform={p.platform} /><strong>{p.competitor_label}</strong><span className="muted">{FORMAT_WORD[p.format]}, {p.posted_at ? formatDate(p.posted_at) : "date unknown"}</span></p>
          <Figures p={p} />
          {p.outlier && (
            <p className="cs-outlier-line">
              <strong>{p.outlier.ratio}x</strong>{" "}
              {p.outlier.basis === "followers"
                ? <>their follower count in views ({compactCount(p.outlier.value)} views, {compactCount(p.author_followers ?? p.outlier.usual)} followers).</>
                : p.outlier.basis === "typical"
                  ? <>the views of a typical post in this search ({compactCount(p.outlier.value)} against {compactCount(Math.round(p.outlier.usual))}).</>
                  : <>their usual {METRIC_WORD[p.outlier.metric]} ({compactCount(p.outlier.value)} against a usual {compactCount(Math.round(p.outlier.usual))}, from their other {p.outlier.samples} posts).</>}
            </p>
          )}
          {p.caption && <p className="cs-lightbox-caption">{p.caption}</p>}
          {p.analysis ? (
            <div className="cs-analysis">
              {p.analysis.hook && <p><strong>Hook.</strong> {p.analysis.hook}</p>}
              <p><strong>Why it worked.</strong> {p.analysis.why}</p>
              {p.analysis.idea && <p><strong>For us.</strong> {p.analysis.idea}</p>}
            </div>
          ) : null}
          {err && <p className="inline-notice danger" role="alert">{err}</p>}
          {draft && <p className="inline-notice success" role="status"><span>Draft saved in {draft.name}'s Content review.</span><Link className="text-button" to={draft.url}>Open it</Link></p>}
          {idea && <p className="inline-notice success" role="status"><span>{idea.existing ? `Already in ${idea.name}'s content pipeline.` : `Saved to ${idea.name}'s content pipeline.`}</span><Link className="text-button" to={idea.url}>Open it</Link></p>}
          {canChange && brands.length > 1 && (
            <label className="cs-for">
              <span>Make this for</span>
              <select value={target} disabled={!!busy} onChange={(e) => { setTarget(Number(e.target.value)); setDraft(null); setIdea(null); }}>
                {brands.map((x) => <option key={x.id} value={x.id}>{x.name}{x.id === clientId ? " (this client)" : x.industry === "Agency" ? " (your agency)" : ""}</option>)}
              </select>
            </label>
          )}
          <div className="cs-lightbox-actions">
            {canChange && !p.analysis && <button type="button" className="button secondary" disabled={!!busy} onClick={() => void run("analyze")}><Sparkles aria-hidden="true" />{busy === "analyze" ? "Reading…" : "Why it worked"}</button>}
            {canChange && !draft && <button type="button" className="button secondary" disabled={!!busy} onClick={() => void run("draft")}>{busy === "draft" ? "Writing…" : "Write our version"}</button>}
            {canChange && !idea && <button type="button" className="button secondary" disabled={!!busy} onClick={() => void run("idea")}><Lightbulb aria-hidden="true" />{busy === "idea" ? "Saving…" : "Save as idea"}</button>}
            {p.url && <a className="button secondary" href={p.url} target="_blank" rel="noreferrer noopener"><ExternalLink aria-hidden="true" />Open on {PLATFORM_WORD[p.platform]}</a>}
          </div>
          {fresh && videoFailed && <p className="muted cs-flag">The video link has expired. Open it on {PLATFORM_WORD[p.platform]}.</p>}
        </div>
      </div>
    </div>
  );
}

type FindProps = { clientId: number; defaults: { service: string; area: string }; canChange: boolean; onAdded: (label: string, hasSocial: boolean) => void };
export function FindView(props: FindProps) {
  return <ClientFindView key={props.clientId} {...props} />;
}
function ClientFindView({ clientId, defaults, canChange, onAdded }: FindProps) {
  const saved = useApi<{ find: Find | null }>(`/api/competitors/find?client_id=${clientId}`);
  const [service, setService] = useState(defaults.service);
  const [area, setArea] = useState(defaults.area);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [find, setFind] = useState<Find | null>(null);
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState<string | null>(null);
  const [fit, setFit] = useState("");
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  useEffect(() => { if (saved.data?.find && !find) setFind(saved.data.find); }, [saved.data, find]);
  async function search(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const result = await api<{ find: Find }>("/api/competitors/find", { method: "POST", body: JSON.stringify({ client_id: clientId, service, area }) });
      if (active.current) { setFind(result.find); setAdded(new Set()); setFit(""); }
    }
    catch (reason) { if (active.current) setErr(reason instanceof Error ? reason.message : "The search did not run."); }
    finally { if (active.current) setBusy(false); }
  }
  async function add(c: Candidate) {
    setAdding(c.key); setErr("");
    try {
      await api("/api/competitors", { method: "POST", body: JSON.stringify({
        client_id: clientId, label: c.name, level: c.fit === "direct" ? 1 : c.fit === "brand" ? 4 : null,
        query: c.page_id ? `https://www.facebook.com/${c.page_id}` : c.name,
        ...(c.instagram_handle ? { instagram_handle: c.instagram_handle } : {}),
      }) });
      if (active.current) { setAdded((s) => new Set(s).add(c.key)); onAdded(c.name, Boolean(c.page_id || c.instagram_handle)); }
    } catch (reason) { if (active.current) setErr(reason instanceof Error ? reason.message : "Could not add."); }
    finally { if (active.current) setAdding(null); }
  }
  const list = (find?.results || []).filter((c) => !fit || c.fit === fit);
  return (
    <div className="cs-view">
      {canChange && (
        <form className="cs-find" onSubmit={search}>
          <label><span>What they do</span><input value={service} onChange={(e) => setService(e.target.value)} placeholder="junk removal" /></label>
          <label><span>Where</span><input value={area} onChange={(e) => setArea(e.target.value)} placeholder="Sacramento" /></label>
          <button type="submit" className="button" disabled={busy || !!adding || `${service.trim()} ${area.trim()}`.trim().length < 3} title="Searches business websites, Instagram and the Ad Library: about 3 Scrape Creators credits."><Search aria-hidden="true" />{busy ? "Searching…" : "Find competitors"}</button>
        </form>
      )}
      <p className="cs-note">Searches business websites, Meta ads and Instagram. Includes national brands even without a social account. About 3 credits a search.{find?.ranked_by_ai ? " The AI sorts the results; check service areas before adding." : ""}</p>
      {err && <p className="inline-notice danger" role="alert">{err}</p>}
      {saved.error && !find && <p className="inline-notice danger" role="alert">Saved search could not load. {saved.error} <button className="text-button" type="button" onClick={saved.reload}>Retry saved search</button></p>}
      {find && find.notes && find.notes.length > 0 && <p className="inline-notice warning">{find.notes.join(" ")}</p>}
      {!find ? (
        <div className="cs-empty" role="status"><strong>{saved.loading ? "Loading saved search…" : busy ? "Searching businesses…" : saved.error ? "Saved search unavailable" : "No search yet"}</strong>{!saved.loading && !busy && !saved.error && <p className="muted">{canChange ? "Say what the client does and where, and press Find competitors." : "An editor can search for businesses to compare here."}</p>}</div>
      ) : (
        <>
          <div className="cs-toolbar">
            <span className="muted">"{find.query}", {formatDate(find.created_at)}: {find.results.length} found</span>
            {find.results.some(c => c.fit) && (
              <select aria-label="Show competitors" value={fit} onChange={(e) => setFit(e.target.value)}>
                <option value="">Everyone</option><option value="direct">Direct competitors</option><option value="nearby">Nearby or related</option><option value="brand">Big brands</option>
              </select>
            )}
          </div>
          {!list.length ? <div className="cs-empty"><strong>{fit ? "No matches for this filter" : "No new businesses found"}</strong><p className="muted">{fit ? "Choose Everyone to see the other results." : "Try a nearby city or a broader service. You can also add a business by name on Compare."}</p></div> : (
            <ul className="cs-finds">
              {list.map((c) => (
                <li key={c.key}>
                  <div className="cs-find-main">
                    <p className="cs-card-who">{c.platform === "website" ? <Globe aria-hidden="true" /> : c.platform === "instagram" ? <Instagram aria-hidden="true" /> : <Facebook aria-hidden="true" />}<strong>{c.name}</strong>{c.instagram_handle && <span className="muted">@{c.instagram_handle}</span>}{c.fit && <span className={`cs-fit fit-${c.fit}`}>{FIT_WORD[c.fit]}</span>}</p>
                    <p className="muted cs-find-facts">{[c.followers != null ? `${compactCount(c.followers)} followers` : null, c.likes != null ? `${compactCount(c.likes)} page likes` : null, c.posts != null ? `${compactCount(c.posts)} posts` : null, c.ads ? `${c.ads} ${c.ads === 1 ? "ad" : "ads"} in the search` : null].filter(Boolean).join(", ")}</p>
                    {c.reason && <p className="cs-find-reason">{c.reason}</p>}
                    {c.about && <p className="muted cs-find-about">{c.about}</p>}
                    {!c.page_id && !c.instagram_handle && <p className="muted cs-find-about">Website found. Ads are unverified; add a social account to read posts.</p>}
                  </div>
                  <div className="cs-find-actions">
                    {c.website && <a className="text-button" href={c.website} target="_blank" rel="noreferrer noopener">{c.source === "Official website" ? "Official website" : "Website"}</a>}
                    {c.instagram_handle && <a className="text-button" href={`https://www.instagram.com/${c.instagram_handle}/`} target="_blank" rel="noreferrer noopener">Instagram</a>}
                    {c.page_id && <a className="text-button" href={`https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=US&view_all_page_id=${c.page_id}`} target="_blank" rel="noreferrer noopener">Their ads</a>}
                    {canChange && (added.has(c.key)
                      ? <span className="muted">Added</span>
                      : <button type="button" className="button secondary" disabled={!!adding || busy} aria-label={`Add ${c.name}`} onClick={() => void add(c)}>{adding === c.key ? "Adding…" : "Add"}</button>)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
